import { useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { inputCls, labelCls, btnPrimary } from "@/lib/constants";
import { toast } from "sonner";
import {
  Save, RotateCcw, Loader2, Image as ImageIcon, Plus, Trash2, Globe,
  Sparkles, Handshake, Building2, Phone, Share2,
} from "lucide-react";

const Section = ({ icon: Icon, title, desc, children }) => (
  <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 sm:p-6 space-y-4">
    <div className="flex items-start gap-3">
      <div className="w-10 h-10 rounded-lg bg-emerald-100 text-emerald-600 flex items-center justify-center flex-shrink-0"><Icon size={20} /></div>
      <div>
        <h2 className="text-lg font-bold text-slate-900 font-heading">{title}</h2>
        {desc && <p className="text-sm text-slate-500">{desc}</p>}
      </div>
    </div>
    {children}
  </div>
);

const Field = ({ label, value, onChange, placeholder, textarea, testId, type = "text" }) => (
  <div>
    <label className={labelCls}>{label}</label>
    {textarea ? (
      <textarea data-testid={testId} rows={3} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className={inputCls} placeholder={placeholder} />
    ) : (
      <input data-testid={testId} type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className={inputCls} placeholder={placeholder} />
    )}
  </div>
);

export default function Website() {
  const [c, setC] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/settings/homepage").then((r) => setC(r.data)).catch(() => toast.error("Failed to load content")).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const set = (path, val) => {
    setC((prev) => {
      const next = JSON.parse(JSON.stringify(prev));
      let obj = next;
      for (let i = 0; i < path.length - 1; i++) obj = obj[path[i]];
      obj[path[path.length - 1]] = val;
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/settings/homepage", { content: c });
      setC(r.data);
      toast.success("Home page content saved");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const reset = async () => {
    if (!window.confirm("Reset all home page content to defaults? This cannot be undone.")) return;
    try {
      const r = await api.post("/settings/homepage/reset");
      setC(r.data);
      toast.success("Content reset to defaults");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const onHeroFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 3 * 1024 * 1024) { toast.error("Image too large (max 3 MB)"); return; }
    const reader = new FileReader();
    reader.onload = () => set(["hero", "image"], reader.result);
    reader.readAsDataURL(file);
  };

  if (loading || !c) return <div className="flex items-center justify-center py-24"><Loader2 className="animate-spin text-emerald-600" size={28} /></div>;

  return (
    <div className="space-y-6 pb-24" data-testid="website-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 font-heading tracking-tight flex items-center gap-2"><Globe size={26} className="text-emerald-600" /> Website Content</h1>
          <p className="text-sm text-slate-500 mt-1">Edit your public home page — hero, stats, partner section, lenders, contact & footer</p>
        </div>
        <div className="flex gap-2">
          <button data-testid="website-reset-button" onClick={reset} className="flex items-center gap-2 px-4 py-2 rounded-lg text-slate-600 hover:bg-slate-100 text-sm font-medium border border-slate-200"><RotateCcw size={15} /> Reset</button>
          <button data-testid="website-save-button" onClick={save} disabled={saving} className={btnPrimary}>{saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Save Changes</button>
        </div>
      </div>

      {/* Hero */}
      <Section icon={Sparkles} title="Hero Section" desc="The top banner of your home page">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Badge text" testId="hero-badge" value={c.hero.badge} onChange={(v) => set(["hero", "badge"], v)} placeholder="Trusted loan distribution partner" />
          <div className="grid grid-cols-2 gap-3">
            <Field label="Title line 1" testId="hero-title" value={c.hero.title} onChange={(v) => set(["hero", "title"], v)} placeholder="Loans made" />
            <Field label="Highlighted text" testId="hero-title-highlight" value={c.hero.title_highlight} onChange={(v) => set(["hero", "title_highlight"], v)} placeholder="simple & fast." />
          </div>
        </div>
        <Field label="Subtitle" testId="hero-subtitle" textarea value={c.hero.subtitle} onChange={(v) => set(["hero", "subtitle"], v)} />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Apply button label" testId="hero-apply-label" value={c.hero.apply_label} onChange={(v) => set(["hero", "apply_label"], v)} />
          <Field label="Partner button label" testId="hero-partner-label" value={c.hero.partner_label} onChange={(v) => set(["hero", "partner_label"], v)} />
        </div>
        <div>
          <label className={labelCls}>Hero background image</label>
          <div className="flex items-center gap-4">
            <img src={c.hero.image} alt="hero preview" className="w-32 h-20 object-cover rounded-lg border border-slate-200 bg-slate-100" />
            <label className="flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-medium cursor-pointer border border-slate-200">
              <ImageIcon size={15} /> Upload image
              <input data-testid="hero-image-upload" type="file" accept="image/*" onChange={onHeroFile} className="hidden" />
            </label>
          </div>
          <p className="text-xs text-slate-400 mt-1.5">PNG/JPG, max 3 MB. Uploaded image replaces the current background.</p>
        </div>
      </Section>

      {/* Stats */}
      <Section icon={Building2} title="Stat Cards" desc="The four highlight cards below the hero">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {c.stats.map((s, i) => (
            <div key={i} className="grid grid-cols-2 gap-3 p-3 rounded-lg bg-slate-50 border border-slate-200">
              <Field label={`Card ${i + 1} value`} testId={`stat-val-${i}`} value={s.val} onChange={(v) => set(["stats", i, "val"], v)} placeholder="24 hrs" />
              <Field label="Label" testId={`stat-label-${i}`} value={s.label} onChange={(v) => set(["stats", i, "label"], v)} placeholder="Fast Approvals" />
            </div>
          ))}
        </div>
      </Section>

      {/* Partner */}
      <Section icon={Handshake} title="Partner Section" desc="The 'Partner with us' block">
        <Field label="Badge text" testId="partner-badge" value={c.partner.badge} onChange={(v) => set(["partner", "badge"], v)} />
        <Field label="Heading" testId="partner-heading" value={c.partner.heading} onChange={(v) => set(["partner", "heading"], v)} />
        <Field label="Description" testId="partner-text" textarea value={c.partner.text} onChange={(v) => set(["partner", "text"], v)} />
        <ListEditor label="Benefits" items={c.partner.benefits} onChange={(v) => set(["partner", "benefits"], v)} testPrefix="benefit" />
        <ListEditor label="Required documents" items={c.partner.documents} onChange={(v) => set(["partner", "documents"], v)} testPrefix="doc" />
      </Section>

      {/* Lenders */}
      <Section icon={Building2} title="Lenders Section" desc="Banks shown here come from your Lenders module — toggle 'Show on home page' per lender. Only the heading & subtext are edited here.">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Heading" testId="lenders-heading" value={c.lenders.heading} onChange={(v) => set(["lenders", "heading"], v)} />
          <Field label="Subtext" testId="lenders-subtext" value={c.lenders.subtext} onChange={(v) => set(["lenders", "subtext"], v)} />
        </div>
      </Section>

      {/* Contact */}
      <Section icon={Phone} title="Contact & Helpdesk" desc="Shown in the footer / contact bar">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Phone number" testId="contact-phone" value={c.contact.phone} onChange={(v) => set(["contact", "phone"], v)} placeholder="+91 98765 43210" />
          <Field label="Email" testId="contact-email" value={c.contact.email} onChange={(v) => set(["contact", "email"], v)} placeholder="support@arjunfintech.com" />
          <Field label="Helpdesk number" testId="contact-helpdesk" value={c.contact.helpdesk} onChange={(v) => set(["contact", "helpdesk"], v)} placeholder="1800-123-4567" />
          <Field label="Address" testId="contact-address" value={c.contact.address} onChange={(v) => set(["contact", "address"], v)} placeholder="City, State" />
        </div>
      </Section>

      {/* Social */}
      <Section icon={Share2} title="Social Media" desc="Leave blank to hide an icon">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {["facebook", "instagram", "linkedin", "twitter", "youtube", "whatsapp"].map((k) => (
            <Field key={k} label={k.charAt(0).toUpperCase() + k.slice(1) + " URL"} testId={`social-${k}`} value={c.social[k]} onChange={(v) => set(["social", k], v)} placeholder={`https://${k}.com/...`} />
          ))}
        </div>
      </Section>

      {/* Footer */}
      <Section icon={Globe} title="Footer" desc="Company name, quick links & copyright">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Company name" testId="footer-company" value={c.footer.company} onChange={(v) => set(["footer", "company"], v)} />
          <Field label="Copyright text" testId="footer-copyright" value={c.footer.copyright} onChange={(v) => set(["footer", "copyright"], v)} />
        </div>
        <div>
          <label className={labelCls}>Footer links</label>
          <div className="space-y-2">
            {c.footer.links.map((lnk, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 items-center">
                <input data-testid={`footer-link-label-${i}`} value={lnk.label} onChange={(e) => set(["footer", "links", i, "label"], e.target.value)} placeholder="Label" className={`${inputCls} col-span-5`} />
                <input data-testid={`footer-link-href-${i}`} value={lnk.href} onChange={(e) => set(["footer", "links", i, "href"], e.target.value)} placeholder="#apply or https://..." className={`${inputCls} col-span-6`} />
                <button onClick={() => set(["footer", "links"], c.footer.links.filter((_, idx) => idx !== i))} className="col-span-1 p-2 rounded-lg text-slate-400 hover:text-rose-600"><Trash2 size={14} /></button>
              </div>
            ))}
          </div>
          <button data-testid="add-footer-link" onClick={() => set(["footer", "links"], [...c.footer.links, { label: "", href: "" }])} className="mt-2 text-xs text-emerald-600 font-medium flex items-center gap-1"><Plus size={13} /> Add link</button>
        </div>
      </Section>

      <div className="flex justify-end gap-2 pt-2">
        <button data-testid="website-save-button-bottom" onClick={save} disabled={saving} className={btnPrimary}>{saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Save Changes</button>
      </div>
    </div>
  );
}

function ListEditor({ label, items, onChange, testPrefix }) {
  return (
    <div>
      <label className={labelCls}>{label}</label>
      <div className="space-y-2">
        {items.map((it, i) => (
          <div key={i} className="flex gap-2 items-center">
            <input data-testid={`${testPrefix}-${i}`} value={it} onChange={(e) => onChange(items.map((x, idx) => idx === i ? e.target.value : x))} className={inputCls} />
            <button onClick={() => onChange(items.filter((_, idx) => idx !== i))} className="p-2 rounded-lg text-slate-400 hover:text-rose-600 flex-shrink-0"><Trash2 size={14} /></button>
          </div>
        ))}
      </div>
      <button data-testid={`add-${testPrefix}`} onClick={() => onChange([...items, ""])} className="mt-2 text-xs text-emerald-600 font-medium flex items-center gap-1"><Plus size={13} /> Add item</button>
    </div>
  );
}
